import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import resetCurrentCartId from "@insite/client-framework/Store/Data/Carts/Handlers/ResetCurrentCartId";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import {
    canAddToCart,
    getProductState,
    hasEnoughInventory,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadDataIfNeeded";
import notifyProductRecs from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/NotifyProductRecs";
import placeOrder from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import setCartId from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCartId";
import setCheckoutPaymentMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCheckoutPaymentMethod";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import preloadOrderConfirmationData from "@insite/client-framework/Store/Pages/OrderConfirmation/Handlers/PreloadOrderConfirmationData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Button from "@insite/mobius/Button";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LoadingOverlayRightStore from "@rightstore/Custom/Components/LoadingOverlayRightStore";
import { datalayerConstants } from "@rightstore/Utilities/constants";
import { attributeTypeGtmReady, datalayerBeginCheckout } from "@rightstore/Utilities/datalayer";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum paymentMethodsEnum {
    invoice = "Invoice",
    credits = "RightSTORE Credits",
}

interface OwnProps {
    labelOverride?: string;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    setOpenModal?: Dispatch<SetStateAction<boolean>>;
    extendedStyles?: ButtonPresentationProps;
}

type Props = OwnProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const { isUpdatingCartLine } = state.pages.cart;
    const selectedVariant = getProductState(state, state.pages.productDetails.selectedProductId);
    const promotionsDataView = getReviewAndPayPromotions(state);
    const { cartId, placeOrderErrorMessage } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const isLoadDataNeeded =
        ((!cartState.value ||
            (cartState.value.lineCount && !cartState.value.cartLines?.length) ||
            !cartState.value.carriers) &&
            !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading);

    return {
        orderConfirmationPageLink: getPageLinkByPageType(state, "OrderConfirmationPage"),
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart || isUpdatingCartLine,
        placeOrderErrorMessage,
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAutoRenewable:
            selectedVariant.value?.properties?.isAutoRenewable ||
            props.productContext.product.properties.isAutoRenewable,
        cart: cartState.value,
        billToState: getBillToState(state, cartState?.value ? cartState.value.billToId : undefined),
        isLoadDataNeeded: isLoadDataNeeded,
        cartId,
        productRecsCalled: state.pages.checkoutReviewAndSubmit.productRecsCalled,
    };
};

const mapDispatchToProps = {
    placeOrder,
    preloadOrderConfirmationData,
    resetCurrentCartId,
    setPlaceOrderErrorMessage,
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    updateCartLine: makeHandlerChainAwaitable<Parameters<typeof updateCartLine>[0], CartLineModel>(updateCartLine),
    loadBillTo,
    setCheckoutPaymentMethod,
    setCartId,
    loadDataIfNeeded,
    notifyProductRecs,
};

export interface QuickFreeOrderButton {
    RequestQuoteErrorMessageText?: TypographyPresentationProps;
    Wrapper?: InjectableCss;
    loader: LoadingSpinnerProps;
    Button?: InjectableCss;
    submitForQuoteButtonTest?: any;
}

export const quickFreeOrderButton: QuickFreeOrderButton = {
    RequestQuoteErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 6px;
            margin-bottom: 15px;
        `,
    },
    loader: {
        size: 16,
    },
    Wrapper: {
        css: css`
            button {
                background: ${getColor("info")};
                border: 1px solid ${getColor("info")};
                border-radius: 6px;
                padding: 12px 32px;
                line-height: 0;
                &:focus {
                    outline-color: transparent;
                }
                &:not([disabled]):hover {
                    background: ${getColor("common.background")};
                    border: 1px solid ${getColor("info")};
                    color: ${getColor("info")};
                }
                &:not([disabled]):hover span {
                    color: ${getColor("info")};
                }
                width: 100%;
            }
        `,
    },
};

const Ownstyles = quickFreeOrderButton;

function QuickFreeOrderButton({
    productContext: {
        product: { id: productId, unitListPrice, productTitle, productNumber, attributeTypes },
        productInfo: { qtyOrdered, unitOfMeasure, failedToLoadInventory, pricing },
    },
    canAddToCart,
    addToCart,
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    placeOrderErrorMessage,
    setPlaceOrderErrorMessage,
    labelOverride,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    notes,
    setOpenModal,
    extendedStyles,
    isAutoRenewable,
    cart,
    preloadOrderConfirmationData,
    resetCurrentCartId,
    history,
    toaster,
    orderConfirmationPageLink,
    updateCartLine,
    billToState,
    placeOrder,
    loadDataIfNeeded,
    setCheckoutPaymentMethod,
    setCartId,
    isLoadDataNeeded,
    cartId,
    productRecsCalled,
    notifyProductRecs,
    ...otherProps
}: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

    const canInvoice = billToState.value ? billToState.value?.properties.canPayByInvoice : undefined;

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    useEffect(() => {
        if (isLoading) {
            if (isLoadDataNeeded) {
                loadDataIfNeeded({ cartId, cartLinesShouldBeExpanded: true });
            } else if (!productRecsCalled) {
                notifyProductRecs({ cartId });
            }
        }
    });

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart?.billToId });
        }
    }, [billToState]);

    function timeout(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // This function needs to stay async to keep the payment method selection logic in sync with the addToCartClickHandler
    async function getPaymentMethod() {
        const paymentMethodInvoice = cart?.paymentOptions?.paymentMethods?.find(
            (method: any) => method.name === paymentMethodsEnum.invoice,
        );
        const paymentMethodCredits = cart?.paymentOptions?.paymentMethods?.find(
            (method: any) => method.name === paymentMethodsEnum.credits,
        );

        if (
            paymentMethodInvoice &&
            canInvoice === "true" &&
            (pricing?.actualPrice !== 0 || isAutoRenewable === "true")
        ) {
            return Promise.resolve(paymentMethodInvoice.name);
        }
        if (paymentMethodCredits && (!isAutoRenewable || isAutoRenewable === "false")) {
            return Promise.resolve(paymentMethodCredits.name);
        }

        return Promise.resolve(undefined);
    }

    async function addToCartClickHandler() {
        setIsLoading(true);
        setPlaceOrderErrorMessage({});

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        await updateCartLine({
            cartLine: {
                ...cartline,
                properties: {
                    isAutoRenewable: isAutoRenewable ?? "false",
                    queryParams: window.location.search.replace("?", ""),
                },
            },
        });

        const dataLayerData = {
            /* eslint-disable */
            item_id: productNumber,
            item_name: productTitle,
            item_brand: datalayerConstants.BRAND,
            item_categories: attributeTypeGtmReady(attributeTypes, "Categories"),
            item_customerTypes: attributeTypeGtmReady(attributeTypes, "CustomerType"),
            price: unitListPrice,
            quantity: 1
        };

        datalayerBeginCheckout({ eventName: "begin_checkout", value: unitListPrice.toString(), currency: datalayerConstants.CURRENCY, data: [dataLayerData] });

        const paymentMethod = await getPaymentMethod();
        if (!paymentMethod) {
            setPlaceOrderErrorMessage({
                errorMessage: "No valid payment methods found or the product is not configuered correctly",
            });
            setIsLoading(false);
            setOpenModal && setOpenModal(false);

            return;
        }

        await timeout(1000); // Needed to make sure the cart has been loaded before placing the order
        placeOrder({
            paymentMethod,
            poNumber: "",
            vatNumber: "",
            saveCard: false,
            cardHolderName: "",
            cardNumber: "",
            cardType: "",
            expirationMonth: 1,
            expirationYear: new Date().getFullYear(),
            securityCode: "",
            useBillingAddress: true,
            address1: "",
            countryId: "",
            stateId: "",
            city: "",
            postalCode: "",
            payPalToken: undefined,
            payPalPayerId: undefined,
            accountHolderName: "",
            accountNumber: "",
            routingNumber: "",
            payerId: "",
            maskedCardNumber: "",
            antiFraudSessionId: "",
            onSuccess: (cartId: string) => {
                preloadOrderConfirmationData({
                    cartId,
                    onSuccess: () => {
                        setPlaceOrderErrorMessage({});
                        if (cart?.isAwaitingApproval) {
                            resetCurrentCartId({});
                        }
                        history.push(`${orderConfirmationPageLink!.url}?cartId=${cartId}`);
                    },
                });
            },
            onError: () => {
                setOpenModal && setOpenModal(false);
                setPlaceOrderErrorMessage({ errorMessage: "Failed to place order" });
                setIsLoading(false);
            },
            onComplete(resultProps: any) {
                if (!resultProps.apiResult?.cart) {
                    this.onError?.();
                    return;
                }
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.(resultProps.apiResult.cart.id);
            },
        });
    }

    return (
        <LoadingOverlayRightStore
            loading={isRedirecting}
            css={css`
                width: 100%;
                height: 100%;
                position: fixed !important;
                top: 0;
                left: 0;
                display: flex;
                justify-content: center;
                align-items: center;
            `}
        >
            <StyledWrapper {...Ownstyles.Wrapper}>
                <Button
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || isLoading}
                    {...otherProps}
                >
                    {isLoading ? <LoadingSpinner {...Ownstyles.loader} /> : labelOverride ?? translate("Request now")}
                </Button>
                {placeOrderErrorMessage && (
                    <Typography {...Ownstyles.RequestQuoteErrorMessageText}>{placeOrderErrorMessage}</Typography>
                )}
            </StyledWrapper>
        </LoadingOverlayRightStore>
    );
}

export default withProductContext(
    connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(QuickFreeOrderButton))),
);
